import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { RanchEvent } from '@shared/ranch-feed';

export interface RanchEventsPayload {
  events: RanchEvent[];
  today: string;
  fetchedAt: string | null;
  stale: boolean;
  ranchUnavailable?: boolean;
  source: {
    name: string;
    url: string;
    rec1Url?: string;
    amenityRentalsUrl?: string;
    hoaNational?: {
      name: string;
      calendarUrl: string;
      memberCentralUrl?: string;
      diningReservationUrl?: string;
      enclaveMenusUrl?: string;
      snapshot?: string;
    };
  };
}

/**
 * Town calendar items from the resident portal RSS feeds, merged with the
 * seeded Babcock National HOA snapshot (`kind: hoa_national`).
 *
 * Unlike alerts, a failed fetch is visible: Home's What's On strip and the
 * What's On page both need to fall back to the official calendar / Rec1
 * links rather than pretending nothing is scheduled. The Worker still
 * returns HOA events if only the town RSS is down.
 */
export function useRanchEvents(days: number) {
  const [data, setData] = useState<RanchEventsPayload | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<RanchEventsPayload>(`/community/events?days=${days}`)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  return { data, error, loading };
}
